import { Box, Typography } from "@mui/material";
import { useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../../app/hooks";
import DialogComponent from "../../../common/components/functional/dialogs/DialogComponent";
import FormSection from "../../../common/components/functional/formSection/FormSection";
import FormTable from "../../../common/components/functional/formTable/FormTable";
import { Info } from "../types/queries";
import { ModalState } from "../types/types";
import DescriptionsForm from "./DescriptionsForm";
import { NAME_SPACE } from "./WorkLocationsForm";

// needed to enable dragging of nested dialogs
const DRAG_HANDLE_SELECTOR = "drag-handle-descriptions";

const DescriptionsTable = () => {
  const [modalState, setModalState] = useState<ModalState>({
    show: false,
    id: "",
  });

  const { t } = useTranslation();

  const translatedWorkTitles = useAppSelector(
    (state) => state.localized.workTitle,
  );

  const { getValues, setValue, watch } = useFormContext();
  const info: Record<string, Info> = watch("info");
  const tableData =
    (info &&
      Object.entries(info).map(([workTitle, { fi, en }]) => ({
        values: { workTitle, fi, en },
        id: workTitle,
        deletable: true,
      }))) ||
    [];

  const formValues = tableData.find(
    (item) => item.id === modalState.id,
  )?.values;

  const headers = [
    { text: t("common:workTitle") },
    { text: t("common:fi") },
    { text: t("common:en") },
  ];
  const addButtonLabel = t(`${NAME_SPACE}:addDescription`);
  const columnWidths = ["10%", "30%", "30%"];

  const onOpen = (id?: string) => setModalState({ show: true, id });
  const onClose = () => setModalState({ show: false, id: "" });

  const handleDelete = (id: string) => {
    const infoObj = getValues("info");
    delete infoObj[id];
    setValue("info", infoObj, { shouldDirty: true });
  };

  const TitleComponent = (
    <Box>
      <Typography variant="header">
        {t(`${NAME_SPACE}:orderDescriptions`)}
      </Typography>
      <Typography
        fontSize="12px"
        color="textSecondary"
        textTransform="capitalize"
      >
        {t("common:optionalDash")}
      </Typography>
    </Box>
  );

  return (
    <FormSection xsLabel={3} xsField={9} title={TitleComponent} titleInside>
      <FormTable
        data={tableData.sort((a, b) =>
          translatedWorkTitles[a.values.workTitle].localeCompare(
            translatedWorkTitles[b.values.workTitle],
          ),
        )}
        headers={headers}
        handleOpenEditModal={(_, id) => onOpen(id)}
        addButtonLabel={addButtonLabel}
        handleDelete={(id) => handleDelete(id)}
        valueMaxLines={3}
        translationKeys={["workTitle"]}
        columnWidths={columnWidths}
      />
      <DialogComponent
        open={modalState.show}
        onClose={onClose}
        dragHandleSelector={DRAG_HANDLE_SELECTOR}
      >
        <DescriptionsForm onClose={onClose} values={formValues} />
      </DialogComponent>
    </FormSection>
  );
};

export default DescriptionsTable;
